import React, { useCallback, useEffect, useState } from "react";
import httpService from "../../../Service/httpService";
import { toast } from "react-toastify";
import StripeEmbedPaymentForm from "../Components/StripeEmbedPaymentForm";
import { CircularProgress } from "@mui/material";
import GuestRegistrationModal from "../../GuestRegistrationModal/GuestRegistrationModal";
import { getUserID } from "../../../Service/getLocalStorage";
import { useDispatch } from "react-redux";
import { isUserLoggedInAction } from "../../../Redux/user/user";
import ApplyGiftCard from "../../ApplyGiftCard";
import { GiftCardPreview } from "../../../Service/giftCard";

const BuyService = ({ serviceParams }: { serviceParams: any }) => {
  const [clientSecret, setClientSecret] = useState("");
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [giftCardCode, setGiftCardCode] = useState("");
  const dispatch = useDispatch();

  const getClientSecret = useCallback(async (userId?: number, appliedGiftCardCode?: string) => {
    const params: Record<string, unknown> = {
      ...serviceParams,
      qty: 1,
      user_id: userId || getUserID(),
    };

    const code = appliedGiftCardCode ?? giftCardCode;
    if (code) {
      params.gift_card_code = code;
    }

    try {
      setIsLoading(true);
      const response: any = await httpService.post("buy-service", params);
      
      if (response?.data?.clientSecret) {
        setClientSecret(response?.data?.clientSecret);
        
        if (response?.data?.user) {
          dispatch(isUserLoggedInAction(true));
        }
      } else {
        const message = response?.data?.message;
        toast.error(Array.isArray(message) ? message.join(", ") : response?.message || "Something went wrong");
      }
    } catch (err: any) {
      console.error("Buy service error:", err);
      
      if (err?.response?.status === 401 || err?.response?.data?.message?.includes("user")) {
        setShowGuestModal(true);
      } else {
        const message = err?.response?.data?.message;
        toast.error(Array.isArray(message) ? message.join(", ") : err?.message || "Something went wrong");
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, giftCardCode, serviceParams]);

  const handleGiftCardApplied = (code: string, _preview: GiftCardPreview | null) => {
    setGiftCardCode(code);
    setClientSecret("");
    if (getUserID()) {
      getClientSecret(undefined, code);
    }
  };

  const handleGuestRegistrationSuccess = (userData: any) => {
    getClientSecret(userData.id);
  };

  const handleGuestRegistrationSubmit = async (guestData: any) => {
    const params: Record<string, unknown> = {
      ...serviceParams,
      qty: 1,
      guest_registration: guestData,
    };

    if (giftCardCode) {
      params.gift_card_code = giftCardCode;
    }

    try {
      setIsLoading(true);
      const response: any = await httpService.post("buy-service", params);
      
      if (response?.data?.clientSecret) {
        setClientSecret(response?.data?.clientSecret);
        
        if (response?.data?.user) {
          localStorage.setItem("Nurture_user_data", JSON.stringify(response.data.user));
          localStorage.setItem("Nurture_user_token", response.data.token);
          
          window.dispatchEvent(new Event("storage"));
          dispatch(isUserLoggedInAction(true));
        }
        
        toast.success("Account created and payment initialized successfully!");
        setShowGuestModal(false);
      } else {
        if (response?.data?.message) {
          const messages = Array.isArray(response.data.message) 
            ? response.data.message 
            : [response.data.message];
          toast.error(messages.join(", "));
        } else {
          toast.error(response?.message || "Something went wrong");
        }
        throw new Error("Registration failed");
      }
    } catch (err: any) {
      console.error("Guest registration error:", err);
      
      if (err?.response?.data?.message) {
        const messages = Array.isArray(err.response.data.message) 
          ? err.response.data.message 
          : [err.response.data.message];
        toast.error(messages.join(", "));
      } else if (err?.response?.data?.errors) {
        const errorMessages = Object.values(err.response.data.errors).flat();
        toast.error(errorMessages.join(", "));
      } else if (err?.message) {
        toast.error(err.message);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const userId = getUserID();
    if (userId) {
      getClientSecret();
    } else {
      setShowGuestModal(true);
    }
  }, [getClientSecret]);

  return (
    <>
      {getUserID() && (
        <ApplyGiftCard
          checkoutType="service"
          product={{
            service_id: serviceParams?.service_id,
            addon_class: serviceParams?.addon_class,
          }}
          selectedCode={giftCardCode}
          onApplied={handleGiftCardApplied}
        />
      )}

      {clientSecret && <StripeEmbedPaymentForm clientSecret={clientSecret} />}
      {!clientSecret && !showGuestModal && (
        <div className="w-full d-flex align-items-center justify-content-center py-5">
          <CircularProgress />
        </div>
      )}
      
      <GuestRegistrationModal
        open={showGuestModal}
        onClose={() => setShowGuestModal(false)}
        onSuccess={handleGuestRegistrationSubmit}
        serviceParams={serviceParams}
      />
    </>
  );
};

export default BuyService;
